import _get from 'lodash/get';

import { CONTROL_PANEL_ENVIRONMENTS } from '../constants';
import { getInLocalStorage } from '..';
import * as environments from './environments';

/**
 * Check for special test environments and return corresponding values. These are used when building
 * the app for the purpose of running integration tests with Cypress in `packages/e2e`.
 * @returns an environment for building the app in test mode or null if the app is not in test mode
 */
const getTestEnvironment = (): Environment | null => {
    if (process.env.REACT_APP_CUSTOM_NODE_ENV === 'test-dev') {
        return 'development';
    }

    if (process.env.REACT_APP_CUSTOM_NODE_ENV === 'test-staging') {
        return 'staging';
    }

    if (process.env.REACT_APP_CUSTOM_NODE_ENV === 'test-prod') {
        return 'production';
    }

    return null;
};

export const isTestEnvironment = (): boolean =>
    ['test-dev', 'test-staging', 'test-prod'].includes(
        process.env?.REACT_APP_CUSTOM_NODE_ENV ?? '__none__',
    );

/**
 * Get the current the current build environment, i.e. 'development', 'staging' or 'production'.
 * This is used to build the app with Webpack, and determines which endpoints are set for the
 * deployed site for a given environment.
 * @remarks This is sourced from REACT_APP_CUSTOM_NODE_ENV in some cases so that we have more
 * flexibility about how the environment is assigned. Without this step, we would always be using
 * 'production' when building the app. Our package.json scripts use `env-cmd` to reference a .env
 * file and get the REACT_APP_CUSTOM_NODE_ENV from there. See packages/web/package.json for examples
 * of this.
 * @returns the environment used to build the app with Webpack {@link Environment}
 */
export const getBuildEnvironment = (): Environment => {
    // getBuildEnvironment is sometimes used outside of getEnvironment, so check for the test
    // environments first
    const testEnvironment = getTestEnvironment();

    if (testEnvironment) {
        return testEnvironment;
    }

    const result =
        process.env.REACT_APP_CUSTOM_NODE_ENV ||
        process.env.NODE_ENV ||
        'development';

    return result as Environment;
};

/**
 * This function checks whether the build environment can allow the Control Panel to set the
 * environment. This is an internal-only tool, so it should only be enabled in `development` and
 * possibly in `staging`. The current logic is determined by the CONTROL_PANEL_ENVIRONMENTS
 * constant.
 * @returns true if the control panel is allowed to set the environment
 */
export const getEnableControlPanel = (): boolean =>
    CONTROL_PANEL_ENVIRONMENTS.includes(getBuildEnvironment());

/**
 * Get the current active environment. The mechanics of this vary based upon whether we're in local
 * development, on a test environment, or building the app for deployment to one of our URLs.
 *
 * Generally speaking, the environment variable returned by this function determines which endpoint
 * to use for a given service. Each service has a `development`, `staging` and `production` version,
 * so we have to support these three environments. We deploy the web app to three separate URLs, one
 * for each of these environments.
 *
 * - When in local development, we use the environment set by the Control Panel, which is
 *   also persisted in localStorage.
 * - When in a test environment,  we only use the environment specified by the `test-<env>`
 *   environment variable, such as `test-dev` for `development`. This means we ignore the
 *   environment set by the Control Panel in localStorage.
 * - When building the app and deploying it, we only use the environment specified in either
 *   `NODE_ENV` or `REACT_APP_CUSTOM_NODE_ENV. Why do we look in these two variables? Because our
 *   tooling (Create React App) is built around the convention of using `NODE_ENV` set to either
 *   `development` or `production`, but we need to support `staging` as well. Create React App
 *   can only build the app in two "modes", development (non-minified JS) or production
 *   (minified JS). We generally use the production (minified JS) build mode but use our own
 *   `REACT_APP_CUSTOM_NODE_ENV` to determine which set of endpoints to use.
 *
 * @returns the current active environment
 */
export const getEnvironment = (): string => {
    // When in test mode, ignore the environment set by the Control Panel that is stored in
    // localStorage
    const testEnvironment = getTestEnvironment();

    if (testEnvironment) {
        return testEnvironment;
    }

    const enableControlPanel = getEnableControlPanel();

    // When the control panel is enabled, use the environment stored in localStorage
    if (enableControlPanel) {
        return getInLocalStorage('environment') ?? 'development';
    }

    // Otherwise, use the build environment
    return getBuildEnvironment();
};

/**
 * Get an environment variable based upon the active environment.
 * @param appKey {@link AppKey}
 * @param envVarKey {@link EnvVarKey}
 * @returns an environment variable, such as an API endpoint
 */
export const getWebpackEnvVar = (
    appKey: AppKey,
    envVarKey: EnvVarKey,
): string => {
    const environment = getEnvironment();

    return _get(environments, [appKey, environment, envVarKey], '');
};

/**
 * Get an environment variable based upon a passed-in environment.
 * @param appKey {@link AppKey}
 * @param envVarKey {@link EnvVarKey}
 * @param testEnvironment {@link Environment}
 * @returns an environment variable, such as an API endpoint
 */
export const getTestEnvVar = (
    appKey: AppKey,
    envVarKey: EnvVarKey,
    testEnvironment: Environment,
): string => _get(environments, [appKey, testEnvironment, envVarKey], '');
